import { useEffect, useRef, useState } from 'react';
import { SceneController } from './scene';
import type { CameraAngle } from '@/config/exercise3d';

interface ViewerProps {
  modelName: string;
  isPaused?: boolean;
  playbackSpeed?: number;
  cameraAngle?: CameraAngle;
  rotationAngle?: number;
  onReady?: () => void;
  onError?: (message: string) => void;
}

export function Viewer({
  modelName,
  isPaused = false,
  playbackSpeed = 1.0,
  cameraAngle = 'front',
  rotationAngle,
  onReady,
  onError,
}: ViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const controllerRef = useRef<SceneController | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const controller = new SceneController(canvas, {
      onStatus: (s, err) => {
        setStatus(s);
        if (s === 'error' && err) {
          setErrorMsg(err);
          onError?.(err);
        } else if (s === 'ready') {
          onReady?.();
        }
      },
    });
    controllerRef.current = controller;
    void controller.init(modelName, cameraAngle).catch(() => {});
    return () => {
      controller.dispose();
      controllerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply prop changes to the controller
  useEffect(() => {
    if (controllerRef.current?.isReady()) {
      void controllerRef.current.setExercise(modelName);
    }
  }, [modelName]);

  useEffect(() => {
    controllerRef.current?.setPaused(isPaused);
  }, [isPaused]);

  useEffect(() => {
    controllerRef.current?.setPlaybackSpeed(playbackSpeed);
  }, [playbackSpeed]);

  useEffect(() => {
    if (rotationAngle !== undefined) {
      controllerRef.current?.setRotation(rotationAngle);
    } else {
      controllerRef.current?.setCameraAngle(cameraAngle);
    }
  }, [rotationAngle, cameraAngle]);

  return (
    <div className="viewer">
      <canvas ref={canvasRef} className="viewer-canvas" />
      {status === 'loading' && <div className="viewer-overlay">Loading…</div>}
      {status === 'error' && (
        <div className="viewer-overlay viewer-overlay--error">
          Failed to load 3D scene.
          {errorMsg && <div className="viewer-overlay-detail">{errorMsg}</div>}
        </div>
      )}
    </div>
  );
}
